/**
 * Declared-outputs editor, shown in flow settings for every flow type.
 *
 * @module FlowDesigner/WorkflowOutputsSettings
 *
 * Historically only seed flows could declare `seedOutputsJson` (the values a
 * seed script hands back after provisioning data), so this editor lived only
 * in the Seed Data Catalog page. The API now accepts `seedOutputsJson` from
 * any flow — a `call_flow` node's caller needs its target's declared outputs
 * to know what `$var.*` names become available after it runs — so this is
 * surfaced here for every flow type, not just seed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { SettingsSection, Text, useToast } from '@chatsdet/ui';
import { SeedOutputsEditor } from '../../components/SeedOutputsEditor';
import {
  useSeedWorkflowOutputs,
  useUpdateSeedOutputs,
  type SeedDataOutput,
} from '../../../hooks/useSeedWorkflowOutputs';

interface WorkflowOutputsSettingsProps {
  workflowId: string;
  /** Whether this is a seed flow — only affects the section's title/copy. */
  isSeedFlow: boolean;
}

/** Debounced save — the editor's `TextField`s fire `onChange` per keystroke,
 *  so writing straight through to the mutation would send one PUT per
 *  character and fight the user's cursor with each refetch. Same ref-timer
 *  shape as `useUndoHistory`'s `debouncedSyncToBackend`. */
const SAVE_DEBOUNCE_MS = 600;

export function WorkflowOutputsSettings({ workflowId, isSeedFlow }: WorkflowOutputsSettingsProps) {
  const { toast } = useToast();
  const { data } = useSeedWorkflowOutputs(workflowId);
  const updateMutation = useUpdateSeedOutputs(workflowId);
  const outputs = data?.outputs ?? [];

  // Local editing state — null means "no local edits, show server data".
  // No path clears it back to null once set: it stays authoritative for the
  // section's mounted lifetime, so the post-save invalidation refetch can
  // never render a stale server value over the user's edits.
  const [localOutputs, setLocalOutputs] = useState<SeedDataOutput[] | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The payload that hasn't been confirmed saved yet — set on every edit,
  // cleared only once the matching save succeeds. Covers both "closed while
  // the debounce timer was still pending" and "the last save failed" — in
  // both cases this ref is still non-null on unmount, so the flush below
  // catches it regardless of which path led there.
  const pendingSaveRef = useRef<SeedDataOutput[] | null>(null);

  // `mutate`'s identity is actually stable across renders in React Query v5
  // (it's returned from a `useCallback` bound to the mutation's observer),
  // but the unmount cleanup below runs with `[]` deps, so it reads the
  // current one through a ref rather than depending on that stability.
  const mutateRef = useRef(updateMutation.mutate);
  mutateRef.current = updateMutation.mutate;

  // `[]` deps, deliberately: this must run ONLY on unmount. Listing a
  // reactive value here would re-run the effect and fire the flush mid-edit,
  // pushing a half-typed value to the server.
  useEffect(() => {
    return () => {
      // FLUSH on unmount, don't cancel. An unsaved payload means either the
      // user typed within the debounce window and then closed the overlay,
      // or the last save failed — dropping either is precisely the silent
      // edit-loss this section was changed to fix.
      //
      // Fire-and-forget: the write goes out and `useUpdateSeedOutputs`'
      // mutation-level invalidations still run (they're attached to the
      // mutation, not the observer), but per-call callbacks are NOT an
      // option here — MutationObserver gates them on `hasListeners()`, which
      // is already false once this component is gone. So a failure on this
      // last-chance save is unreported by design; surfacing it would take a
      // mutationCache-level handler that outlives the component.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const pending = pendingSaveRef.current;
      if (pending) mutateRef.current(pending);
    };
  }, []);

  const handleChange = useCallback(
    (next: SeedDataOutput[]) => {
      setLocalOutputs(next);
      pendingSaveRef.current = next;

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        // Null the handle as soon as it fires so the cleanup above doesn't
        // clearTimeout a spent one. It does NOT make the flush exclusive: if
        // the overlay closes while this save is still in flight, the same
        // payload is sent twice, since only success clears pendingSaveRef.
        // That's an idempotent PUT of an identical body, so it's left alone
        // rather than tracked with another ref.
        //
        // Two saves overlapping by more than the debounce AND their responses
        // arriving out of order would leave the server holding the older
        // array. Unobserved, so deliberately unguarded — noted so the next
        // reader knows it was considered, not missed.
        debounceTimerRef.current = null;
        updateMutation.mutate(next, {
          onSuccess: () => {
            // Only clear if this is still the latest edit — a newer
            // keystroke may have landed mid-flight and set a different
            // pending payload that still needs saving.
            if (pendingSaveRef.current === next) pendingSaveRef.current = null;
          },
          onError: () => toast.error('Could not save outputs'),
        });
      }, SAVE_DEBOUNCE_MS);
    },
    [updateMutation.mutate, toast]
  );

  return (
    <SettingsSection
      title={isSeedFlow ? 'Seed outputs' : 'Outputs'}
      description={
        isSeedFlow
          ? 'Values this seed script hands back after provisioning data, available as $var.* to consuming flows.'
          : 'Values this flow hands back to its caller, available as $var.* to a "Run a flow" node that calls it.'
      }
    >
      {(localOutputs ?? outputs).length === 0 && (
        <Text size="xs" className="text-muted-foreground">
          No outputs declared yet.
        </Text>
      )}
      <SeedOutputsEditor outputs={localOutputs ?? outputs} onChange={handleChange} />
      {updateMutation.isPending && (
        <Text size="xs" className="text-muted-foreground">
          Saving…
        </Text>
      )}
      {updateMutation.isError && (
        <Text size="xs" className="text-destructive">
          Could not save outputs.
        </Text>
      )}
    </SettingsSection>
  );
}
